import React, { useState } from 'react';
import { Clock, AlertTriangle, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export type MetaTokenStatus = 'EXPIRING_SOON' | 'EXPIRED' | string;

interface MetaTokenExpiryBannerProps {
  tokenStatus: MetaTokenStatus;
  tokenExpiry?: string | null;
  adAccountName?: string | null;
  onDismiss?: () => void;
  /** "‹ 1/2 ›" controls when shown inside the top-of-app banner carousel. */
  carouselControls?: React.ReactNode;
}

function formatExpiryDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

export const MetaTokenExpiryBanner: React.FC<MetaTokenExpiryBannerProps> = ({
  tokenStatus,
  tokenExpiry,
  adAccountName,
  onDismiss,
  carouselControls,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  if (dismissed) return null;

  const isExpired = tokenStatus === 'EXPIRED';
  const expiryDate = formatExpiryDate(tokenExpiry);
  const label = adAccountName || 'Your Meta ad account';

  const message = isExpired
    ? `${label}'s Meta access token has expired. Ad delivery is paused until you reconnect.`
    : expiryDate
      ? `${label}'s Meta access token expires ${expiryDate}. Reconnect to keep ads running.`
      : `${label}'s Meta access token will expire soon. Reconnect to keep ads running.`;

  const bgClass = isExpired ? 'bg-red-600' : 'bg-amber-500';
  const dismissHoverClass = isExpired ? 'hover:bg-red-500' : 'hover:bg-amber-400';
  const ctaTextClass = isExpired ? 'text-red-600' : 'text-amber-700';

  return (
    <div
      className={`w-full ${bgClass} text-white px-4 py-2.5 flex items-center justify-between gap-3 z-50`}
      data-testid="meta-token-expiry-banner"
      data-status={isExpired ? 'expired' : 'expiring-soon'}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isExpired ? (
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        ) : (
          <Clock className="h-4 w-4 flex-shrink-0" />
        )}
        <span className="text-sm font-medium truncate">{message}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="secondary"
          className={`bg-white ${ctaTextClass} hover:bg-gray-100 text-xs font-semibold`}
          onClick={() => navigate('/home/ads/setup')}
          data-testid="meta-token-expiry-banner-reconnect-cta"
        >
          <ExternalLink className="h-3.5 w-3.5 mr-1" />
          Reconnect Meta
        </Button>
        {carouselControls}
        <button
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className={`p-1 rounded-full ${dismissHoverClass} transition-colors`}
          aria-label="Dismiss banner"
          data-testid="meta-token-expiry-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
